import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getAllNotifications, getUnreadNotificationCount, markAllNotificationsRead, markNotificationRead } from '../../store/actions';
import { linkedItemPath, NOTIFICATION_TYPE, PRIORITY, label } from '../../constants/garmentCodes';

const TYPE_ICONS = {
  payment_receipt: '💰',
  payment_receipt_under_review: '💰',
  low_balance: '⚠',
  design_approval: '◈',
  design_version_submitted: '◈',
  design_charge_submitted: '◈',
  receipt_confirmed: '✓',
  fabric_approved: '◉',
  fabric_submitted: '◉',
  task_overdue: '⏰',
  adjustment_pending: '≡',
  order_update: '▣',
  clarification_requested: '?',
};

export default function NotificationList({ basePath }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [filter, setFilter] = useState('all');
  const notifications = useSelector((state) => state.notification.notifications);
  const loading = useSelector((state) => state.notification.loading);
  const unread = useSelector((state) => state.notification.unreadCount || 0);

  useEffect(() => {
    dispatch(getAllNotifications(filter === 'unread' ? { read: false } : {}));
    dispatch(getUnreadNotificationCount());
  }, [dispatch, filter]);

  const open = (n) => {
    if (!n.read) dispatch(markNotificationRead({ id: n.id }));
    const path = linkedItemPath(basePath, n.linkedItemType);
    if (path) navigate(path);
  };

  return (
    <>
      <div className="flex items-center gap-3 mb-4">
        <div className="flex gap-1 border border-gray-200 rounded-lg overflow-hidden">
          <button onClick={() => setFilter('all')} className={`px-4 py-1.5 text-[12px] font-medium ${filter === 'all' ? 'bg-gray-100 text-gray-800' : 'text-gray-400 hover:bg-gray-50'}`}>All</button>
          <button onClick={() => setFilter('unread')} className={`px-4 py-1.5 text-[12px] font-medium ${filter === 'unread' ? 'bg-gray-100 text-gray-800' : 'text-gray-400 hover:bg-gray-50'}`}>Unread ({unread})</button>
        </div>
        {loading && <span className="text-[11px] text-gray-400">Loading…</span>}
        <button onClick={() => dispatch(markAllNotificationsRead())} disabled={unread === 0} className="ml-auto text-[12px] text-blue-600 hover:underline disabled:text-gray-300 disabled:no-underline">Mark all read</button>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden divide-y divide-gray-50">
        {!loading && notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <span className="text-4xl mb-3">🔔</span>
            <p className="text-[14px] font-medium text-gray-700">All caught up</p>
            <p className="text-[12px] text-gray-400 mt-1">No {filter === 'unread' ? 'unread ' : ''}notifications</p>
          </div>
        ) : (
          notifications.map((n) => {
            const typeKey = label(NOTIFICATION_TYPE, n.type, '');
            const priority = label(PRIORITY, n.priority, '');
            return (
              <div key={n.id} onClick={() => open(n)} className={`flex items-start gap-4 px-6 py-4 hover:bg-gray-50 cursor-pointer ${!n.read ? 'bg-blue-50/40' : ''}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-xl flex-shrink-0 ${!n.read ? 'bg-blue-100' : 'bg-gray-100'}`}>{TYPE_ICONS[typeKey] ?? '●'}</div>
                <div className="flex-1 min-w-0">
                  <p className={`text-[13px] leading-snug ${!n.read ? 'font-medium text-gray-900' : 'text-gray-700'}`}>{n.title}</p>
                  {n.body && <p className="text-[12px] text-gray-500 mt-0.5">{n.body}</p>}
                  {priority === 'High' && <span className="inline-block mt-1 text-[10px] font-medium text-red-600 bg-red-50 px-1.5 py-0.5 rounded">High priority</span>}
                </div>
                {!n.read && <div className="w-2 h-2 rounded-full bg-blue-500 mt-2 flex-shrink-0" />}
              </div>
            );
          })
        )}
      </div>
    </>
  );
}
